/**
 * Window Bridge for Planner Service
 * 
 * Bridges planner service functions to the window object for cross-context communication
 * in Office Add-ins (dialog ↔ taskpane/commands runtime).
 * 
 * This allows the PlanningApp component (from shared package) to access
 * planner service functions without direct imports.
 */

import {
  initializePlannerService,
  createPlanningConversation,
  sendMessageToPlanner,
  getCurrentPlan,
  setCurrentPlan,
  resetPlannerService,
  getCurrentJobId,
  getCurrentStyleGuide
} from "../plannerService";
import { backendClient } from "../../../services/communication/backendClient";
import { liveTracker } from "../../../services/tracking/liveTracker";
import { getDeckRegistry, DeckInfo } from "../../../services/tracking/deckRegistry";
import { PresentationPlan } from "@carousel-office/shared/types/planning";
import { externalFilesCache } from "../../../services/externalFilesCache";
import { getContextResolver } from "../../../main/core/contextResolver";

// ═══════════════════════════════════════════════════════════════════════════
// PLAN UPDATE CALLBACK MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

type PlanUpdateCallback = (plan: PresentationPlan, action: 'created' | 'updated') => void;
const planUpdateCallbacks = new Set<PlanUpdateCallback>();

/**
 * Register a callback to receive plan updates
 * @returns Unsubscribe function
 */
function registerPlanUpdateCallback(callback: PlanUpdateCallback): () => void {
  planUpdateCallbacks.add(callback);
  console.log(`[PlannerWindowBridge] Registered plan update callback (total: ${planUpdateCallbacks.size})`);
  
  return () => {
    planUpdateCallbacks.delete(callback);
    console.log(`[PlannerWindowBridge] Unregistered plan update callback (total: ${planUpdateCallbacks.size})`);
  };
}

/**
 * Notify all registered callbacks about a plan update
 */
function notifyPlanUpdate(plan: PresentationPlan, action: 'created' | 'updated'): void {
  console.log(`[PlannerWindowBridge] Notifying ${planUpdateCallbacks.size} callbacks about plan ${action}`);
  planUpdateCallbacks.forEach(callback => {
    try {
      callback(plan, action);
    } catch (error) {
      console.error('[PlannerWindowBridge] Error in plan update callback:', error);
    }
  });
}

/**
 * Bridges planner service functions to the window object
 * 
 * According to Notion docs: Planning Modal instantiates Planner Agent (100% exclusive usage)
 * 
 * @param window - The window object to attach functions to (defaults to global window)
 */
export function bridgePlannerServiceToWindow(window: Window = globalThis.window): void {
  // Expose image resolver for shared tool-call UI previews.
  window.__getContextImageData = async (imageId: string) => {
    try {
      const resolver = getContextResolver();
      const image = resolver.getImage(imageId);
      if (!image?.base64) {
        return null;
      }
      return {
        base64: image.base64,
        mimeType: image.mimeType || "image/png",
      };
    } catch (error) {
      console.warn(`[PlannerWindowBridge] Failed to resolve context image "${imageId}":`, error);
      return null;
    }
  };

  // Store chat-composer images in ContextResolver so they can be sent as multimodal context.
  window.__storeChatInputImage = async (
    base64: string,
    mimeType: string,
    filename?: string
  ): Promise<{ contextId: string; mimeType: string } | null> => {
    try {
      const normalizedMimeType = mimeType.toLowerCase();
      if (normalizedMimeType !== "image/png" && normalizedMimeType !== "image/jpeg") {
        console.warn(
          `[PlannerWindowBridge] Rejecting unsupported chat image mime type: ${mimeType}. ` +
          "Expected image/png or image/jpeg."
        );
        return null;
      }
      const normalizedBase64 = (base64 || "").trim();
      if (!normalizedBase64 || normalizedBase64.startsWith("data:")) {
        console.warn(
          "[PlannerWindowBridge] Rejecting chat image payload: expected raw base64 string payload."
        );
        return null;
      }

      const resolver = getContextResolver();
      const summary = filename
        ? `User-attached image: ${filename}`
        : "User-attached image";
      const contextId = resolver.store("asset", {
        summary,
        filename,
        _imageId: "",
      });
      await resolver.storeImageAsync(contextId, normalizedBase64, normalizedMimeType, {
        source: "chat_input",
        filename,
      });
      resolver.storeWithId(contextId, {
        summary,
        filename,
        _imageId: contextId,
        _imageIds: [contextId],
        mimeType: normalizedMimeType,
      });
      return { contextId, mimeType: normalizedMimeType };
    } catch (error) {
      console.error("[PlannerWindowBridge] Failed to store chat input image:", error);
      return null;
    }
  };

  // Remove chat-composer image context when user deletes attachment before sending.
  window.__deleteContextImage = async (contextId: string): Promise<boolean> => {
    try {
      const resolver = getContextResolver();
      const imageDeleted = resolver.deleteImage(contextId);
      resolver.delete(contextId);
      return imageDeleted;
    } catch (error) {
      console.warn(`[PlannerWindowBridge] Failed to delete context image "${contextId}":`, error);
      return false;
    }
  };

  // Expose all planner service functions
  window.__initializePlannerService = initializePlannerService;
  window.__createPlanningConversation = createPlanningConversation;
  window.__sendMessageToPlanner = sendMessageToPlanner;
  window.__getCurrentPlan = getCurrentPlan;
  window.__setCurrentPlan = setCurrentPlan;
  window.__resetPlannerService = resetPlannerService;
  window.__getCurrentJobId = getCurrentJobId;
  window.__getCurrentStyleGuide = getCurrentStyleGuide;
  
  // Expose plan update callback functions
  window.__registerPlanUpdateCallback = registerPlanUpdateCallback;
  window.__notifyPlanUpdate = notifyPlanUpdate;
  
  // Expose Plans API methods from backendClient
  window.__plansApi_createPlan = () => backendClient.createPlan();
  window.__plansApi_listPlans = (status?: string) => backendClient.listPlans(status);
  window.__plansApi_getPlan = (planId: string) => backendClient.getPlan(planId);
  window.__plansApi_updatePlan = (planId: string, planJson: Record<string, unknown>, targetSlides?: number, label?: string, title?: string, revertToVersion?: number) =>
    backendClient.updatePlan(planId, planJson, targetSlides, label, title, revertToVersion);
  window.__plansApi_deletePlan = (planId: string) => backendClient.deletePlan(planId);
  window.__plansApi_uploadStyleGuide = (planId: string, file: File) =>
    backendClient.uploadStyleGuide(planId, file);
  
  // Parse style guide with automatic slides_base64 extraction for live mode
  window.__plansApi_parseStyleGuide = async (
    planId: string,
    options: {
      isLive: boolean;
      styleGuidedocumentId?: string;
      styleGuidePresentationPath?: string;
      styleGuidePresentationName?: string;  // Display name for the style guide
    }
  ) => {
    let slidesBase64: string[] | undefined;
    
    // For live mode, fetch slide images from liveTracker's storage (reuse existing instance)
    if (options.isLive && options.styleGuidedocumentId) {
      try {
        // Use liveTracker to get snapshots - it shares storage with the tracking system
        const status = await liveTracker.getStatus();
        console.log(`[PlannerWindowBridge] LiveTracker status:`, status);
        
        // Access storage through getDeckRegistry which also uses the same storage
        const registry = getDeckRegistry();
        // Refresh to ensure we have latest data
        await registry.refresh();
        
        // Get snapshots directly from liveTracker's internal storage access
        // Since we need the raw snapshots, we use the registry's internal storage
        const deckInfo = registry.getDeckById(options.styleGuidedocumentId);
        if (!deckInfo) {
          console.warn(`[PlannerWindowBridge] Deck ${options.styleGuidedocumentId} not found in registry`);
        }
        
        // Get snapshots through the storage that's already initialized
        const { LiveTrackingStorage } = await import('../../../services/tracking/liveTrackingStorage');
        const storage = new LiveTrackingStorage();
        await storage.initialize(); // This is fast if already initialized (idempotent)
        const snapshots = await storage.getDeckSnapshots(options.styleGuidedocumentId);
        
        // Sort by slideIndex and extract pptxBase64 (NOT imageBase64!)
        // The backend expects actual PPTX data to parse, not PNG images
        const sortedSnapshots = snapshots.sort((a, b) => a.slideIndex - b.slideIndex);
        
        // Log what we have before filtering
        console.log(`[PlannerWindowBridge] Snapshots before filtering:`, sortedSnapshots.map(s => ({
          slideIndex: s.slideIndex,
          hasPptxBase64: !!s.pptxBase64,
          pptxSizeKB: s.pptxBase64 ? Math.round(s.pptxBase64.length / 1024) : 0,
          hasImageBase64: !!s.imageBase64,
          imageSizeKB: s.imageBase64 ? Math.round(s.imageBase64.length / 1024) : 0,
        })));
        
        slidesBase64 = sortedSnapshots
          .map(s => s.pptxBase64)
          .filter(pptx => pptx && pptx.length > 0);
        
        console.log(`[PlannerWindowBridge] Extracted ${slidesBase64.length} slide PPTX files for live parse (sizes: ${slidesBase64.map(p => Math.round(p.length / 1024) + 'KB').join(', ')})`);
      } catch (error) {
        console.error('[PlannerWindowBridge] Error extracting slide images:', error);
        throw new Error('Failed to extract slide images for live parse');
      }
    }
    
    return backendClient.parseStyleGuide(planId, {
      isLive: options.isLive,
      styleGuidedocumentId: options.styleGuidedocumentId,
      styleGuidePresentationPath: options.styleGuidePresentationPath,
      styleGuidePresentationName: options.styleGuidePresentationName,
      slidesBase64,
    });
  };
  
  // =========================================================================
  // Deck Registry Methods
  // =========================================================================
  
  /**
   * Get all tracked decks from the DeckRegistry
   * Used by ChooseStyleGuide to show available presentations
   */
  window.__getTrackedDecks = async (): Promise<DeckInfo[]> => {
    try {
      const registry = getDeckRegistry();
      // Refresh to ensure we have the latest data
      await registry.refresh();
      const currentdocumentId = registry.getCurrentdocumentId();
      const decks = registry.getAllDecks();
      
      console.log(`[PlannerWindowBridge] Returning ${decks.length} tracked decks`, {
        currentdocumentId,
        decks: decks.map(d => ({
          id: d.id,
          title: d.title,
          isCurrent: d.isCurrent,
        })),
      });
      
      return decks;
    } catch (error) {
      console.error('[PlannerWindowBridge] Error getting tracked decks:', error);
      return [];
    }
  };
  
  /**
   * Get the file path of the currently open presentation
   * Used when building with a "live" style guide (fallback)
   */
  window.__getCurrentDocumentPath = async (): Promise<string | null> => {
    try {
      const documentUrl = (Office.context.document as any)?.url as string | undefined;
      console.log('[PlannerWindowBridge] Current document URL:', documentUrl);
      return documentUrl || null;
    } catch (error) {
      console.error('[PlannerWindowBridge] Error getting current document path:', error);
      return null;
    }
  };
  
  /**
   * Get slide base64 data for a deck from IndexedDB
   * Used when building with a "live" style guide - sends base64 to Python to compile
   */
  window.__getSlideBase64sForDeck = async (documentId: string): Promise<string[] | null> => {
    try {
      console.log('[PlannerWindowBridge] Getting slide base64s for deck:', documentId);
      
      // Import and create LiveTrackingStorage instance
      // This follows the same pattern as __plansApi_parseStyleGuide
      const { LiveTrackingStorage } = await import('../../../services/tracking/liveTrackingStorage');
      const storage = new LiveTrackingStorage();
      await storage.initialize(); // Fast if already initialized (idempotent)
      
      const snapshots = await storage.getDeckSnapshots(documentId);
      console.log(`[PlannerWindowBridge] Found ${snapshots.length} snapshots for deck ${documentId}`);
      
      if (snapshots.length === 0) {
        console.warn('[PlannerWindowBridge] No snapshots found for deck');
        return null;
      }
      
      // Sort by slideIndex and extract pptxBase64
      const sortedSnapshots = snapshots.sort((a, b) => a.slideIndex - b.slideIndex);
      const base64s = sortedSnapshots.map(s => s.pptxBase64).filter((pptx): pptx is string => !!pptx && pptx.length > 0);
      
      console.log(`[PlannerWindowBridge] Returning ${base64s.length} slide base64s (sizes: ${base64s.map(p => Math.round(p.length / 1024) + 'KB').join(', ')})`);
      return base64s;
    } catch (error) {
      console.error('[PlannerWindowBridge] Error getting slide base64s:', error);
      return null;
    }
  };
  
  // =========================================================================
  // RAG Upload Service (via ragUploadService)
  // =========================================================================
  
  // Import is at the top level - we'll lazy import to avoid circular deps
  let ragUploadServiceInstance: typeof import('../../../services/ragUploadService').ragUploadService | null = null;
  
  const getRagUploadService = async () => {
    if (!ragUploadServiceInstance) {
      const module = await import('../../../services/ragUploadService');
      ragUploadServiceInstance = module.ragUploadService;
    }
    return ragUploadServiceInstance;
  };
  
  /**
   * Phase1 direct upload:
   * Upload file blobs and return conflict details before commit.
   */
  window.__detectRagUploadConflicts = async (files: File[]): Promise<{
    upload_id: string;
    files: Array<{
      item_id: string;
      filename: string;
      file_size: number;
      content_hash: string;
      conflict_type: 'none' | 'same_content' | 'name_conflict' | 'name_and_content';
      default_action: 'keep' | 'replace' | 'skip';
      requires_decision: boolean;
      duplicate_of_item_id?: string | null;
      existing_matches: Array<{
        filepath: string;
        filename: string;
        file_id: string;
        content_hash?: string | null;
        last_vectorized?: string | null;
      }>;
    }>;
    total_files: number;
    conflicts_count: number;
  }> => {
    try {
      const service = await getRagUploadService();
      return await service.detectUploadConflicts(files);
    } catch (error) {
      console.error('[PlannerWindowBridge] RAG phase1 conflict detection error:', error);
      throw error;
    }
  };

  /**
   * Phase2 direct upload:
   * Apply decisions and start indexing.
   */
  window.__commitRagUploads = async (
    uploadId: string,
    decisions: Array<{
      item_id: string;
      action: 'keep' | 'replace' | 'skip';
      replace_filepaths?: string[];
    }>,
    workbookId?: string
  ): Promise<{
    status: string;
    files: Array<{
      filepath: string;
      filename: string;
      file_size?: number;
      status: 'processing' | 'error' | 'skipped';
      message: string;
    }>;
    total: number;
    processing: number;
    errors: number;
    skipped?: number;
  }> => {
    try {
      const service = await getRagUploadService();
      return await service.commitUploads(uploadId, decisions, workbookId);
    } catch (error) {
      console.error('[PlannerWindowBridge] RAG phase2 commit error:', error);
      throw error;
    }
  };
  
  /**
   * Cancel RAG file uploads.
   */
  window.__cancelRagUploads = async (filepaths: string[]): Promise<void> => {
    try {
      console.log(`[PlannerWindowBridge] Cancelling ${filepaths.length} RAG uploads`);
      const service = await getRagUploadService();
      await service.cancelUploads(filepaths);
    } catch (error) {
      console.error('[PlannerWindowBridge] RAG cancel error:', error);
      throw error;
    }
  };
  
  /**
   * Register a handler for RAG progress updates.
   * Returns an unsubscribe function.
   */
  window.__onRagProgress = async (handler: (progress: {
    filepath: string;
    filename: string;
    current: number;
    total: number;
    stage: 'uploading' | 'embedding' | 'finalizing' | 'batch' | 'complete' | 'error';
    progress: number;
    error?: string;
  }) => void): Promise<() => void> => {
    const service = await getRagUploadService();
    service.startListening();
    service.onProgress(handler);
    
    // Return unsubscribe function
    return () => {
      service.offProgress(handler);
    };
  };
  
  /**
   * Get list of indexed RAG files.
   */
  window.__getIndexedRagFiles = async (): Promise<Array<{
    filepath: string;
    filename: string;
    file_index: number;
    last_vectorized: string;
  }>> => {
    try {
      const service = await getRagUploadService();
      return await service.getIndexedFiles();
    } catch (error) {
      console.error('[PlannerWindowBridge] Error getting indexed files:', error);
      return [];
    }
  };

  // Log exposure for debugging
  const exposedFunctions = {
    hasContextImageData: !!window.__getContextImageData,
    hasInitialize: !!window.__initializePlannerService,
    hasCreateConversation: !!window.__createPlanningConversation,
    hasSendMessage: !!window.__sendMessageToPlanner,
    hasGetCurrentPlan: !!window.__getCurrentPlan,
    hasSetCurrentPlan: !!window.__setCurrentPlan,
    hasReset: !!window.__resetPlannerService,
    hasGetCurrentJobId: !!window.__getCurrentJobId,
    hasGetCurrentStyleGuide: !!window.__getCurrentStyleGuide,
    hasRegisterPlanUpdateCallback: !!window.__registerPlanUpdateCallback,
    hasNotifyPlanUpdate: !!window.__notifyPlanUpdate,
    // Plans API
    hasPlansApiCreate: !!window.__plansApi_createPlan,
    hasPlansApiList: !!window.__plansApi_listPlans,
    hasPlansApiGet: !!window.__plansApi_getPlan,
    hasPlansApiUpdate: !!window.__plansApi_updatePlan,
    hasPlansApiDelete: !!window.__plansApi_deletePlan,
    hasPlansApiUploadStyleGuide: !!window.__plansApi_uploadStyleGuide,
    hasPlansApiParseStyleGuide: !!window.__plansApi_parseStyleGuide,
    // Deck Registry
    hasGetTrackedDecks: !!window.__getTrackedDecks,
    // RAG Upload
    hasDetectRagUploadConflicts: !!window.__detectRagUploadConflicts,
    hasCommitRagUploads: !!window.__commitRagUploads,
    hasCancelRagUploads: !!window.__cancelRagUploads,
    hasOnRagProgress: !!window.__onRagProgress,
    hasGetIndexedRagFiles: !!window.__getIndexedRagFiles,
    hasStoreChatInputImage: !!window.__storeChatInputImage,
    hasDeleteContextImage: !!window.__deleteContextImage,
  };

  console.log('[Dialog] Planner service functions bridged to window:', exposedFunctions);

  // Verify all functions were exposed successfully
  const missingFunctions = Object.entries(exposedFunctions)
    .filter(([_, exists]) => !exists)
    .map(([name]) => name);

  if (missingFunctions.length > 0) {
    console.warn('[Dialog] Some planner service functions failed to bridge:', missingFunctions);
  }
  
  // =========================================================================
  // Initialize External Files Cache on Startup
  // =========================================================================
  
  // Load cached external files on startup (non-blocking)
  console.log('[PlannerWindowBridge] Initializing external files cache...');
  externalFilesCache.refresh().then(() => {
    const files = externalFilesCache.getFiles();
    console.log(`[PlannerWindowBridge] External files cache initialized: ${files.length} files`);
  }).catch((err) => {
    console.warn('[PlannerWindowBridge] Failed to initialize external files cache:', err);
  });
}

/**
 * Verifies that all planner service functions are bridged to window
 * @returns true if all functions are available, false otherwise
 */
export function verifyPlannerServiceBridge(window: Window = globalThis.window): boolean {
  const required = [
    '__initializePlannerService',
    '__createPlanningConversation',
    '__sendMessageToPlanner',
    '__getCurrentPlan',
    '__setCurrentPlan',
    '__resetPlannerService'
  ] as const;

  const missing = required.filter(name => !window[name as keyof Window]);

  if (missing.length > 0) {
    console.warn('[Dialog] Missing planner service functions in bridge:', missing);
    return false;
  }

  return true;
}

// Legacy exports for backward compatibility (can be removed after updating all references)
export const exposePlannerService = bridgePlannerServiceToWindow;
export const verifyPlannerServiceExposed = verifyPlannerServiceBridge;

